import { useRef, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { AUFGABEN_AKTIONEN as AUFGABEN_KATALOG, LOGBUCH_AKTIONEN, label, schrittFuerAktion, vorschlagTitel } from "../review/aktionFelder"
import { Check, Loader2, Sparkles } from "lucide-react"
import { toast } from "@/components/portal/Toast"
import { ActionConfirmDialog, type ConfirmLine } from "@/components/portal/ActionConfirmDialog"
import { ActionPlanCard } from "@/components/review/ActionPlanCard"
import { VorschlagKompakt } from "@/components/portal/VorschlagKompakt"
import { antwortAuszug, hauptKnopfText, vorschlagSatz } from "@/components/portal/vorschlagSatz"
import { usePlanSpeichern } from "@/hooks/usePlanSpeichern"
import type { CatalogEntry } from "@/components/review/types"
import { buildBodyWithQuote, buildHtmlWithQuote } from "@/lib/replyQuote"
import { AusfuehrenAbgelehnt, ausfuehrenUrl, pruefeAusfuehrung } from "@/lib/ausfuehrenAbgelehnt"
import { useCacheMitleser } from "@/lib/cacheMitleser"
import { HeroLinkModal } from "@/components/portal/HeroLinkModal"
import type { ActionItem, ActionPlan, DraftDetail, ExecutePlanResult } from "@/components/review/types"

/** Die beiden zusaetzlichen Terminvorschlaege im Plan. */
type SlotKey = "slot_secondary" | "slot_tertiary"

/**
 * Der Agent-Plan im Posteingang.
 *
 * Zeigt **dieselbe** `ActionPlanCard` wie das Review — mit klickbaren Feldern,
 * Tagesansicht, Monteur-Chips und dem Ermittlungs-Block. Der erste Anlauf war
 * eine eigene, schlankere Karte; das Ergebnis waren zwei Oberflaechen fuer
 * dieselbe Sache, deren Unterschiede einzeln nachgezogen werden mussten
 * (Zitat, Ueberbuchen, Erledigt-Marker). Der urspruengliche Plan sagte
 * woertlich "ActionPlanCard in den Posteingang" — das ist jetzt so.
 *
 * Zwei bewusste Unterschiede bleiben:
 *
 * 1. **Kein Plan wird automatisch erzeugt.** Erst wird geprueft, ob einer
 *    vorliegt (`only_cached`, 204 = keiner). Eine Mail zu oeffnen darf keinen
 *    Claude-Call kosten — im Posteingang landet auch die Haelfte, bei der es
 *    nichts zu tun gibt. Das Review plant beim Oeffnen, weil dort nur Mails
 *    mit Entwurf gelistet sind.
 * 2. **Was nach aussen geht, ist abgewaehlt.** Den Text liest man im Review.
 *
 * Der Query-Schluessel ist derselbe wie im Review (`["draft-plan", id]`),
 * damit die Bearbeiten-Dialoge der Karte auch hier den Plan aktualisieren.
 */

export function InboxActionPlan({ emailId, kopfExtra, onAntwortAnsehen, threadMails, onMailOeffnen, kompakt, terminUeberholt, karte }: {
  emailId: number
  /** Neues Postfach (28.10.2026): über der vollen Karte steht groß, was der
   *  Agent vorhat, mit Begründung zum Aufklappen — die Schritte bleiben
   *  darunter vollständig sichtbar und änderbar. */
  karte?: boolean
  /** #197: Zur Mail steht schon ein Termin — die Terminvorschläge im Plan
   *  sind überholt und bleiben eingeklappt (mit Knopf zum Aufklappen). */
  terminUeberholt?: boolean
  /** Rechts im Kartenkopf — der Historie-Badge aus der Detailansicht. */
  kopfExtra?: React.ReactNode
  /** Mail-Programm (Variante A): erst den Plan als einen Satz zeigen, die
   *  volle Karte hinter „Alle Schritte". Das Review zeigt weiter die Karte. */
  kompakt?: boolean
  /** "Ansehen" an der Antwort-Zeile klappt den Entwurf darunter auf. */
  onAntwortAnsehen?: () => void
  /** Die Mails des Verlaufs. Der Posteingang oeffnet je Verlauf die neueste
   *  Mail — der offene Plan haengt aber oft an einer aelteren (44 von 252
   *  Review-Mails, 06.09.2026). Die Karte sagt dann, WO er liegt; die
   *  Auswahl selbst bleibt das Abbild des Postfachs. */
  threadMails?: ThreadMailKurz[]
  onMailOeffnen?: (id: number) => void
}) {
  const qc = useQueryClient()
  const [abgewaehlt, setAbgewaehlt] = useState<Set<string>>(new Set())
  const [ueberholteZeigen, setUeberholteZeigen] = useState(false)
  const [alleSchritte, setAlleSchritte] = useState(false)
  const [warumOffen, setWarumOffen] = useState(false)
  const [dialog, setDialog] = useState<{ lines: ConfirmLine[]; needsDouble: boolean } | null>(null)
  const [erledigt, setErledigt] = useState<string[]>([])
  const [mitVerlauf, setMitVerlauf] = useState(true)
  // Verwerfen loescht den Entwurf. `confirm()` blockiert die Automatisierung,
  // also fragt der Knopf selbst nach: erst "Verwerfen", dann "Wirklich?".
  const [verwerfenGefragt, setVerwerfenGefragt] = useState(false)
  /** Slot belegt — Rueckfrage vor dem bewussten Ueberbuchen. */
  const [ueberbuchen, setUeberbuchen] = useState<ConfirmLine[] | null>(null)
  // #194: „An diese Mail ging gerade schon eine Antwort" — bewusst bestaetigen.
  const [doppelt, setDoppelt] = useState<ConfirmLine[] | null>(null)
  const letzteAusfuehrung = useRef<{ forceBook?: boolean; trotzDoppelt?: boolean }>({})
  // Die Terminvorschlaege werden einzeln abgewaehlt — und zwar direkt im Plan.
  // Ein lokaler Zustand daneben waere eine zweite Wahrheit: der Knopf "Neu
  // generieren" schickt den Plan ans LLM, und der muss die Auswahl kennen,
  // sonst stehen wieder Termine in der Mail, die niemand bucht.
  const abgewaehlteSlots = useRef<Partial<Record<SlotKey, ActionPlan["slot"]>>>({})

  // Gibt es ueberhaupt einen Plan? Diese Frage kostet nichts.
  const hatPlan = useQuery({
    queryKey: ["inbox-has-plan", emailId],
    queryFn: async (): Promise<boolean> => {
      const r = await fetch(`/api/v1/drafts/${emailId}/plan?only_cached=true`, { credentials: "include" })
      if (r.status === 204) return false
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      qc.setQueryData(["draft-plan", emailId], await r.json())
      return true
    },
    staleTime: 1000 * 60 * 5,
  })

  // Ab hier derselbe Cache wie im Review — sonst zeigen die Bearbeiten-Dialoge
  // der Karte ihre Aenderung nur dort und hier nicht.
  const planQuery = useQuery<ActionPlan>({
    queryKey: ["draft-plan", emailId],
    queryFn: async () => {
      const r = await fetch(`/api/v1/drafts/${emailId}/plan`, { credentials: "include" })
      if (!r.ok) throw new Error(await fehlertext(r))
      return await r.json() as ActionPlan
    },
    enabled: hatPlan.data === true,
    staleTime: 1000 * 60 * 5,
  })

  const detailQuery = useQuery<DraftDetail>({
    queryKey: ["draft-detail", emailId],
    queryFn: async () => {
      const r = await fetch(`/api/v1/drafts/${emailId}`, { credentials: "include" })
      if (!r.ok) throw new Error(await fehlertext(r))
      return await r.json() as DraftDetail
    },
    enabled: hatPlan.data === true,
    staleTime: 1000 * 60 * 5,
  })

  /** Aktionen, die in HERO als Aufgabe landen — sie haben einen Empfaenger.
   *  `aufgabe_anlegen` hat einen eigenen Plan-Schritt und bleibt hier dabei. */
  const AUFGABEN_AKTIONEN = new Set([...AUFGABEN_KATALOG, "aufgabe_anlegen"])
  // Wer die Aufgabe bekommt. Ohne Angabe nimmt der Executor den Haupt-Monteur
  // des Betriebs — das stand nirgends, man sah es erst hinterher in HERO.
  const [zielUser, setZielUser] = useState<Record<string, number | null>>({})

  // Die Mail selbst — nur aus dem Cache, den die Detailansicht ohnehin haelt.
  // Interessant sind hier die HERO-Verknuepfungen.
  //
  // 🔴 Zwei Anlaeufe sind hier schon gescheitert, beide an derselben Ursache:
  // ein Mitleser darf die Abfrage nicht mitdefinieren. Eine Dummy-`queryFn`
  // schrieb ihr `{}` ueber die echte Mail (leere Detailflaeche); `skipToken`
  // nahm dem Eigentuemer die Funktion weg und liess die Mail am 02.09.2026
  // mit „Missing queryFn" gar nicht mehr aufgehen. `useCacheMitleser` meldet
  // deshalb keinen Observer an — siehe `lib/cacheMitleser.ts`.
  const emailDaten = useCacheMitleser<{ email?: { tenant_slug?: string; hero_contact_id?: number | null; hero_project_id?: number | null } }>(
    ["email", String(emailId)],
  )
  const verknuepft = emailDaten?.email
  // #189: „Kontakt anlegen“ ohne gefundenen Kontakt — bestehenden in HERO
  // suchen und verknuepfen, derselbe Dialog wie in der Mailansicht.
  const [verknuepfenOffen, setVerknuepfenOffen] = useState(false)

  // Was HERO zum Kontakt hat. Derselbe Schluessel wie in der Metaspalte —
  // React Query holt das nicht zweimal.
  const heroProjekte = useQuery<{
    projects?: { id: number; code?: string; name?: string }[]
    /** Mehrere Kontakte teilen dasselbe Merkmal — der Mensch waehlt. */
    candidates?: { id: number; first_name?: string; last_name?: string; email?: string; phone?: string; address?: string }[]
    ambiguous_on?: string
  }>({
    queryKey: ["email-hero-projects", emailId],
    queryFn: async () => {
      const r = await fetch(`/api/v1/emails/${emailId}/hero-projects`, { credentials: "include" })
      if (!r.ok) return { projects: [] }
      return r.json()
    },
    staleTime: 1000 * 30,
  })

  const monteure = useQuery<{ items?: { id: number; hero_user_id: number | null; name: string; main?: boolean }[] }>({
    queryKey: ["monteure", detailQuery.data?.tenant_slug || ""],
    queryFn: async () => {
      const t = detailQuery.data?.tenant_slug || ""
      if (!t) return { items: [] }
      const r = await fetch(`/api/v1/calendar/monteure?tenant=${encodeURIComponent(t)}`, { credentials: "include" })
      if (!r.ok) return { items: [] }
      return r.json()
    },
    enabled: !!detailQuery.data?.tenant_slug,
    staleTime: 1000 * 60 * 5,
  })

  const createPlan = useMutation({
    mutationFn: async () => {
      const r = await fetch(`/api/v1/drafts/${emailId}/plan`, { credentials: "include" })
      if (!r.ok) throw new Error(await fehlertext(r))
      return await r.json() as ActionPlan
    },
    onSuccess: (p) => {
      qc.setQueryData(["draft-plan", emailId], p)
      qc.setQueryData(["inbox-has-plan", emailId], true)
      setAbgewaehlt(new Set())
      setErledigt([])
    },
    onError: (e) => toast("Plan konnte nicht erstellt werden: " + msg(e), "error"),
  })

  const plan = planQuery.data ?? null
  const detail = detailQuery.data ?? null
  const planSpeichern = usePlanSpeichern(emailId)
  const alle: ActionItem[] = plan?.actions ?? []

  /**
   * Aktionen, die der Planer NICHT vorgesehen hat.
   *
   * Bis 21.08.2026 zeigte die Karte ausschliesslich geplante Aktionen. Wer
   * eine Mail MIT Terminvorschlag vor sich hatte, fuer die der Planer keinen
   * passenden Slot fand, sah keinen Weg zu buchen — und schloss daraus, es
   * gehe nicht (#12).
   *
   * Aufgeteilt, weil "alle anzeigen" woertlich 17 ausgegraute Zeilen unter
   * zwei echten waeren: die vier mit festem Platz stehen offen, der Rest liegt
   * hinter einem Aufklapper.
   */
  const katalog = useQuery<{ items: CatalogEntry[] }>({
    queryKey: ["actions-catalog"],
    queryFn: async () => {
      const r = await fetch("/api/v1/drafts/actions/catalog", { credentials: "include" })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      return r.json()
    },
    staleTime: 60 * 60 * 1000,   // der Katalog aendert sich mit dem Deploy, nicht im Betrieb
  })
  const NACHTRAG_FEST = ["kontakt_anlegen", "projekt_anlegen", "termin_buchen", "aufgabe_anlegen"]
  const nachtragbar = (() => {
    const geplant = new Set(alle.map((a) => a.type))
    const offenImKatalog = (katalog.data?.items ?? [])
      .filter((k) => k.implemented && !geplant.has(k.key))
      .sort((a, b) => a.order - b.order)
    return {
      fest: offenImKatalog.filter((k) => NACHTRAG_FEST.includes(k.key)),
      weitere: offenImKatalog.filter((k) => !NACHTRAG_FEST.includes(k.key)),
    }
  })()
  /**
   * Gegenstandslos, weil die Mail schon verknuepft ist.
   *
   * Der Executor nimmt ein bestehendes Projekt (`reuse_project`), sobald
   * `email_log.hero_project_id` steht — die Aktion "Projekt anlegen" laeuft
   * dann gar nicht mehr. Sie angehakt stehen zu lassen hiesse, etwas zu
   * versprechen, was nicht passiert.
   */
  const schonVerknuepft = (type: string): boolean => {
    if (type === "projekt_anlegen") return !!verknuepft?.hero_project_id
    if (type === "kontakt_anlegen") return !!verknuepft?.hero_contact_id
    return false
  }
  const istFertig = (a: ActionItem) =>
    a.done === true || erledigt.includes(a.type) || schonVerknuepft(a.type)
  const offen = alle.filter((a) => !istFertig(a))
  const gewaehlt = offen.filter((a) => istAn(a.type, abgewaehlt))

  // Wer im Empfaenger-Feld steht, wenn niemand etwas gewaehlt hat. Dieselbe
  // Regel wie in der Zeile unten (`standard`).
  const empfaengerListe = (monteure.data?.items || []).filter((m) => m.hero_user_id != null)
  const standardEmpfaenger = empfaengerListe.find((m) => m.main) || empfaengerListe[0]

  /** Den gewaehlten Empfaenger an die Aufgaben-Aktionen haengen.
   *
   *  #149: Steht nichts gewaehlt, zeigt das Feld den Standard des Betriebs —
   *  dann geht er auch mit. Bisher ging in dem Fall NICHTS mit, und der
   *  Server nahm nur `main_partner_id`; ohne den (oder mit Haupt-Monteur ohne
   *  HERO-User) entstand die Aufgabe ohne Empfaenger, obwohl im Feld ein Name
   *  stand. Was man sieht, wird geschickt. */
  function mitEmpfaenger(liste: ActionItem[]): ActionItem[] {
    return liste.map((a) => {
      if (!AUFGABEN_AKTIONEN.has(a.type) || a.type === "aufgabe_anlegen") return a
      const uid = zielUser[a.type] ?? standardEmpfaenger?.hero_user_id ?? null
      if (!uid) return a
      const name = (monteure.data?.items || []).find((m) => m.hero_user_id === uid)?.name || ""
      return { ...a, params: { ...(a.params || {}), target_user_id: uid, target_user_name: name } }
    })
  }

  /** Slots nach der Vorschlags-Wahl beschneiden + Antwort mit Verlauf — wie im Review. */
  function planFuerVersand(): ActionPlan | null {
    if (!plan) return null
    const leer = { start_datetime: "", duration_minutes: 45, partner_id: null }
    let p: ActionPlan = { ...plan }
    p = {
      ...p,
      slot_secondary: plan.slot_secondary || leer,
      slot_tertiary: plan.slot_tertiary || leer,
    }
    // #149: dasselbe fuer `aufgabe_anlegen` — der Empfaenger steht dort im
    // Plan-Schritt `monteur_task`, nicht in den Aktions-Parametern.
    if (p.monteur_task && p.monteur_task.target_user_id == null && standardEmpfaenger?.hero_user_id) {
      p = {
        ...p,
        monteur_task: {
          ...p.monteur_task,
          target_user_id: standardEmpfaenger.hero_user_id,
          target_user_name: standardEmpfaenger.name,
        },
      }
    }
    if (gewaehlt.some((a) => a.type === "antwort_senden") && plan.reply?.body && detail) {
      p = {
        ...p,
        reply: {
          ...plan.reply,
          body: buildBodyWithQuote(plan.reply.body, detail, mitVerlauf),
          body_html: buildHtmlWithQuote(plan.reply.body, detail, mitVerlauf),
        },
      }
    }
    return p
  }

  const execute = useMutation({
    mutationFn: async (args?: { forceBook?: boolean; trotzDoppelt?: boolean }) => {
      const p = planFuerVersand()
      if (!p) throw new Error("Kein Plan geladen")
      letzteAusfuehrung.current = args ?? {}
      const r = await fetch(ausfuehrenUrl(emailId, args), {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ plan: p, actions: mitEmpfaenger(gewaehlt) }),
      })
      await pruefeAusfuehrung(r)
      if (!r.ok) throw new Error(await fehlertext(r))
      return await r.json() as ExecutePlanResult
    },
    onSuccess: (result) => {
      setDialog(null)
      // Nur abhaken, was der Server als gelaufen meldet — `completed` spricht
      // Executor-Sprache, die Karte Katalog-Sprache.
      const gelaufen = result.executed_actions ?? []
      if (gelaufen.length) setErledigt((e) => [...new Set([...e, ...gelaufen])])
      const offengeblieben = (result.results?.not_executed as string[] | undefined) ?? []
      const belegt = result.failed?.error === "slots_stale"
        || result.failed?.error === "slot_live_busy"

      if (!result.ok && belegt) {
        // Der User darf bewusst ueberbuchen — aber im eigenen Dialog, nicht in
        // einem `window.confirm()`. Das blockierte den ganzen Tab, bis jemand
        // klickt: kein Screenshot, kein Test, keine Automatisierung kam daran
        // vorbei. Und es sah aus wie ein Browser-Fehler, nicht wie unsere
        // Rueckfrage.
        const wann = plan?.slot?.start_datetime?.replace("T", " ").slice(0, 16) || "der Termin"
        setUeberbuchen([
          { type: "termin_buchen", text: `Der Termin am ${wann} ist belegt.`, risk: "external" },
          ...(result.failed?.message
            ? [{ type: "hinweis", text: result.failed.message, risk: "internal" } as ConfirmLine]
            : []),
          {
            type: "hinweis",
            text: "Trotzdem buchen? Der Termin wird mit dem Haupt-Monteur angelegt, auch wenn er zu der Zeit schon etwas hat.",
            risk: "internal",
          },
        ])
        return
      } else if (!result.ok) {
        toast(`Fehler bei Schritt ${result.failed?.step}: ${result.failed?.error}`, "error")
      } else if (offengeblieben.length) {
        toast(`${gelaufen.length} ausgefuehrt, ${offengeblieben.length} nicht: ${offengeblieben.join(", ")}`, "warn")
      } else {
        toast(`✓ Ausgefuehrt: ${gelaufen.join(", ") || "nichts"}`)
      }
      qc.invalidateQueries({ queryKey: ["email", String(emailId)] })
      qc.invalidateQueries({ queryKey: ["draft-plan", emailId] })
      qc.invalidateQueries({ queryKey: ["emails"] })
    },
    onError: (e) => {
      setDialog(null)
      if (e instanceof AusfuehrenAbgelehnt && e.code === "plan_veraltet") {
        // #194: nichts ausgefuehrt — den neuen Plan holen, der Mensch prueft ihn.
        toast(e.message, "warn")
        qc.invalidateQueries({ queryKey: ["draft-plan", emailId] })
        return
      }
      if (e instanceof AusfuehrenAbgelehnt && e.code === "doppelte_antwort") {
        setDoppelt([
          { type: "antwort_senden", text: e.message, risk: "external" },
          { type: "hinweis", text: "Nichts wurde ausgefuehrt. „Bestätigen“ sendet die zweite Antwort und fuehrt den Plan aus.", risk: "internal" },
        ])
        return
      }
      toast("Ausfuehren fehlgeschlagen: " + msg(e), "error")
    },
  })

  // Neu planen — der Gegenspieler zum gecachten Plan. Ohne ihn bleibt ein Plan
  // fuer immer auf dem Stand seiner Entstehung, auch wenn in HERO inzwischen
  // Kontakt, Projekt oder Termine dazugekommen sind. Das Review hat den Weg
  // laengst (`/replan`), der Posteingang bisher nicht.
  const neuPlanen = useMutation({
    mutationFn: async (stark?: boolean) => {
      const r = await fetch(`/api/v1/drafts/${emailId}/replan`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ hint: "", current_plan: plan, stark: !!stark }),
      })
      if (!r.ok) throw new Error(await fehlertext(r))
      return await r.json() as ActionPlan
    },
    onSuccess: (p) => {
      qc.setQueryData(["draft-plan", emailId], p)
      abgewaehlteSlots.current = {}
      setAbgewaehlt(new Set())
      setErledigt([])
      qc.invalidateQueries({ queryKey: ["email-hero-projects", emailId] })
      toast(p.eskaliert ? "✓ Neu geplant mit dem stärkeren Modell" : "✓ Plan neu erstellt")
    },
    onError: (e) => toast("Neu planen fehlgeschlagen: " + msg(e), "error"),
  })

  /** Kontakt aus der Auswahl verknuepfen.
   *
   * Derselbe Endpunkt, den auch „Zuordnen" in der Metaspalte benutzt. Danach
   * ist die Mehrdeutigkeit aufgeloest: `hero-projects` laedt den Kontakt beim
   * naechsten Mal direkt ueber die ID und muss nicht mehr suchen.
   */
  const zuordnen = useMutation({
    mutationFn: async (contactId: number) => {
      const r = await fetch(`/api/v1/emails/${emailId}/hero-link`, {
        method: "PATCH", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ contact_id: contactId }),
      })
      if (!r.ok) throw new Error(await fehlertext(r))
      return await r.json()
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["email-hero-projects", emailId] })
      qc.invalidateQueries({ queryKey: ["email", String(emailId)] })
      toast("✓ Kontakt verknüpft")
    },
    onError: (e) => toast("Verknüpfen fehlgeschlagen: " + msg(e), "error"),
  })

  /** Entwurf verwerfen — leert den Text und setzt die Mail auf 'gesehen'. */
  const verwerfen = useMutation({
    mutationFn: async () => {
      const r = await fetch(`/api/v1/drafts/${emailId}/discard`, { method: "POST", credentials: "include" })
      if (!r.ok) throw new Error(await fehlertext(r))
      return await r.json()
    },
    onSuccess: () => {
      toast("Entwurf verworfen — Mail steht auf 'gesehen'")
      qc.invalidateQueries({ queryKey: ["email", String(emailId)] })
      qc.invalidateQueries({ queryKey: ["emails"] })
    },
    onError: (e) => toast("Verwerfen fehlgeschlagen: " + msg(e), "error"),
  })

  const vorschau = useMutation({
    mutationFn: async () => {
      const r = await fetch(`/api/v1/drafts/${emailId}/preview-actions`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ plan: planFuerVersand(), actions: mitEmpfaenger(gewaehlt) }),
      })
      if (!r.ok) throw new Error(await fehlertext(r))
      return await r.json() as { lines: ConfirmLine[]; needs_double_confirmation: boolean }
    },
    onSuccess: (v) => setDialog({ lines: v.lines, needsDouble: v.needs_double_confirmation }),
    onError: (e) => toast("Vorschau fehlgeschlagen: " + msg(e), "error"),
  })

  if (hatPlan.isLoading) return null

  // Haengt der offene Plan an einer anderen Mail dieses Verlaufs? Die
  // neueste davon — bei mehreren ist das die, die das Review als naechstes
  // zeigt.
  const planAnderswo = planAndererMail(emailId, threadMails)

  if (hatPlan.data === false) {
    return (
      <div className="ev2-hero-card">
        <div className="ev2-hero-card-title">
          <Sparkles className="w-3.5 h-3.5" /> Agent-Plan
        </div>
        <div className="ev2-hero-card-body">
          <div style={{ fontSize: 12.5, color: "var(--text-secondary)", marginBottom: 8 }}>
            Fuer diese Mail gibt es noch keinen Plan.
          </div>
          {planAnderswo && <PlanAndererMailHinweis mail={planAnderswo} onOeffnen={onMailOeffnen} />}
          <button className="ev2-hero-ibtn ev2-hero-ibtn-primary"
            onClick={() => createPlan.mutate()} disabled={createPlan.isPending}>
            {createPlan.isPending
              ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Erstelle…</>
              : <><Sparkles className="w-3.5 h-3.5" /> Plan erstellen</>}
          </button>
        </div>
      </div>
    )
  }

  if (!plan || !detail) return null

  const hatTermin = alle.some((a) => a.type === "termin_buchen" && !istFertig(a))
  const zeigtAntwort = gewaehlt.some((a) => a.type === "antwort_senden")
  const alleErledigt = alle.length > 0 && offen.length === 0

  // Vorschlaege, die es im Plan wirklich gibt — "3" anzubieten, wenn nur zwei
  // Slots vorliegen, hiess einen Klick anzubieten, der nichts tut. Abgewaehlte
  // stehen weiter in der Liste (aus dem Zwischenspeicher), nur eben ohne Haken.
  const weitereSlots: SlotKey[] = (["slot_secondary", "slot_tertiary"] as const)
    .filter((k) => !!plan[k]?.start_datetime || !!abgewaehlteSlots.current[k]?.start_datetime)
  const slotAn = (k: SlotKey) => !!plan[k]?.start_datetime
  const aktiveSlots = hatTermin ? 1 + weitereSlots.filter(slotAn).length : 0
  // #197: überholte Terminvorschläge zurücktreten lassen. Nichts wird am Plan
  // verändert — nur die Anzeige klappt ein.
  const hatVorschlaege = !!plan.slot?.start_datetime || weitereSlots.length > 0
  const vorschlaegeEingeklappt = !!terminUeberholt && hatVorschlaege && !ueberholteZeigen

  // Der Plan wird gecacht geladen (`only_cached`) — er kennt den Stand vom
  // Zeitpunkt seiner Erstellung. Legt jemand in der Zwischenzeit ein Projekt
  // an, will der Plan trotzdem ein neues: der Executor nutzt ein bestehendes
  // nur ueber `plan.project.id` oder `email_log.hero_project_id`, und beide
  // sind dann leer. Ergebnis waere ein Doppel-Projekt in HERO.
  const vorhandeneProjekte = heroProjekte.data?.projects || []
  const kandidaten = heroProjekte.data?.candidates || []
  const mehrdeutigAuf = heroProjekte.data?.ambiguous_on || ""
  const legtProjektAn = gewaehlt.some((a) => a.type === "projekt_anlegen") && !plan.project?.id
  const projektWaereDoppelt = legtProjektAn && vorhandeneProjekte.length > 0

  // Der Antworttext nennt die Termine im Fliesstext. Wird einer abgewaehlt,
  // steht er trotzdem noch in der Mail — dann lieber sagen als stumm senden.
  const textNenntAbgewaehlten = weitereSlots
    .filter((k) => !slotAn(k))
    .some((k) => {
      const iso = abgewaehlteSlots.current[k]?.start_datetime || ""
      if (!iso) return false
      const [j, m, t] = iso.slice(0, 10).split("-")
      const body = plan.reply?.body || ""
      return body.includes(`${t}.${m}.${j}`) || body.includes(`${t}.${m}.`)
    })

  return (
    <>
      {/* „Keine Aktion noetig" fuer DIESE Mail heisst nicht, dass im Verlauf
          nichts offen ist — die Kundenanfrage davor kann einen Plan tragen. */}
      {plan.scenario === "skip" && planAnderswo && (
        <div className="ev2-hinweiszeile">
          <PlanAndererMailHinweis mail={planAnderswo} onOeffnen={onMailOeffnen} />
        </div>
      )}
      {/* Eine Karte: erkannte Absicht, ein Satz Begruendung, die Aktionen als
          Checkbox-Zeilen, unten die Primaeraktion. Was frueher "KI-Analyse",
          "Agent-Plan" und "Was jetzt ausgefuehrt wird" auf drei Karten
          verteilt hat, steht hier einmal. */}
      {/* Kompakt nur, wenn nichts zu entscheiden ist. Mehrdeutiger Kontakt,
          drohendes Doppel-Projekt oder ein Antworttext mit abgewähltem Termin
          brauchen die volle Karte — die zeigt den Hinweis an Ort und Stelle. */}
      {terminUeberholt && hatVorschlaege && (
        <div className="ev2-hinweiszeile" data-testid="ueberholte-vorschlaege">
          Terminvorschläge überholt — der Termin steht schon.{" "}
          <button type="button" className="ev2-pk-item-edit"
            onClick={() => setUeberholteZeigen((z) => !z)}>
            {ueberholteZeigen ? "Vorschläge ausblenden" : "Vorschläge trotzdem anzeigen"}
          </button>
        </div>
      )}
      {karte && !alleErledigt && offen.length > 0 && (() => {
        // „Ein Klick · Agent ist sicher“ stand doppelt da (Topf links, Plakette
        // im Schritt) — die Karte nennt die Sicherheit nur noch, wenn der
        // Agent dich braucht (Paul 28.10.).
        const sicher = gewaehlt.length > 0 && plan.confidence === "high"
        const quellen = [...new Set((plan.research?.log || []).map((l) => l.tool))]
        return (
          <section className="pk-vorschlag" data-testid="karte-vorschlag">
            <div className="pk-vorschlag-zeile">
              <span className="pk-agent-marke">Vorschlag des Agenten</span>
              {!sicher && <span className="pk-pill hand">Agent braucht dich</span>}
              <span className="pk-vorschlag-extra">{kopfExtra}</span>
            </div>
            <h3 className="pk-vorschlag-satz">
              {vorschlagSatz(plan, gewaehlt.length ? gewaehlt : offen, (pid) =>
                pid == null ? undefined : (monteure.data?.items || []).find((m) => m.id === pid)?.name)}
            </h3>
            {plan.reasoning && (
              <>
                <button type="button" className="pk-link" aria-expanded={warumOffen}
                  onClick={() => setWarumOffen(!warumOffen)}>
                  {warumOffen ? "Begründung ausblenden" : "Warum? Begründung und Quellen"}
                </button>
                {warumOffen && (
                  <div className="pk-warum">
                    <p>{plan.reasoning}</p>
                    {plan.research?.summary && <p>{plan.research.summary}</p>}
                    {quellen.length > 0 && (
                      <p className="pk-leise">Nachgesehen mit: {quellen.join(", ")} · Details unten unter „Woher stammt das?“</p>
                    )}
                  </div>
                )}
              </>
            )}
          </section>
        )
      })()}
      {kompakt && !alleSchritte && !alleErledigt && offen.length > 0
        && !vorschlaegeEingeklappt
        && kandidaten.length <= 1 && !projektWaereDoppelt && !(zeigtAntwort && textNenntAbgewaehlten) ? (() => {
        // Hat der Planer nichts vorab angehakt („Bitte prüfen"), wird nicht
        // blind ausgeführt: der Knopf führt zum Antworttext bzw. zu den Schritten.
        const pruefen = gewaehlt.length === 0
        const basis = pruefen ? offen : gewaehlt
        const antwortOffen = basis.some((x) => x.type === "antwort_senden") && !!plan.reply?.body
        return (
        <VorschlagKompakt
          satz={vorschlagSatz(plan, basis, (pid) =>
            pid == null ? undefined : (monteure.data?.items || []).find((m) => m.id === pid)?.name)}
          warum={plan.reasoning}
          pruefen={pruefen}
          antwort={antwortOffen ? antwortAuszug(plan.reply?.body) : undefined}
          knopfText={pruefen ? (antwortOffen ? "Antwort ansehen" : "Schritte prüfen") : hauptKnopfText(gewaehlt)}
          anzahlSchritte={alle.length}
          busy={vorschau.isPending || execute.isPending}
          kopfExtra={kopfExtra}
          onAusfuehren={() => {
            if (!pruefen) vorschau.mutate()
            else if (antwortOffen && onAntwortAnsehen) onAntwortAnsehen()
            else setAlleSchritte(true)
          }}
          onAlleSchritte={() => setAlleSchritte(true)}
          onAntwort={onAntwortAnsehen}
          onNeuPlanen={() => neuPlanen.mutate(false)}
          neuPlanenBusy={neuPlanen.isPending}
        />
        )
      })() : (
      <ActionPlanCard
        kompakt
        karte={karte}
        plan={vollstaendig(plan)}
        detail={detail}
        editable
        onPlanChange={(next) => {
          qc.setQueryData(["draft-plan", emailId], next)
          // Auch hier speichern (#40) — die Bearbeiten-Dialoge der Karte
          // aendern Kontakt, Vorgang und Termin, nicht nur die Haken.
          planSpeichern(next as unknown as Record<string, unknown>)
        }}
        kopfExtra={kopfExtra}
        onAntwortAnsehen={onAntwortAnsehen}
        terminUeberholt={vorschlaegeEingeklappt}
        weitereSlots={weitereSlots.map((k) => ({
          key: k,
          an: slotAn(k),
          // Abgewaehlt heisst leer im Plan — angezeigt wird trotzdem der
          // Termin, um den es geht, sonst stuende dort nur ein Strich.
          slot: slotAn(k) ? plan[k] : abgewaehlteSlots.current[k],
        }))}
        onToggleSlot={(k) => {
          const leer = { start_datetime: "", duration_minutes: 45, partner_id: null }
          if (slotAn(k)) {
            abgewaehlteSlots.current[k] = plan[k]
            qc.setQueryData(["draft-plan", emailId], { ...plan, [k]: leer })
          } else {
            qc.setQueryData(["draft-plan", emailId], { ...plan, [k]: abgewaehlteSlots.current[k] || leer })
          }
        }}
        nachtragbar={nachtragbar}
        aktionen={alle.map((a) => {
          const istAufgabe = AUFGABEN_AKTIONEN.has(a.type)
          const istMonteurTask = a.type === "aufgabe_anlegen"
          const liste = empfaengerListe
          const standard = standardEmpfaenger
          // #149: sichtbarer Hinweis am Vorschlag, BEVOR jemand bestaetigt.
          // Ohne Mitarbeiter = es gibt niemanden, den das Feld zeigen koennte
          // (kein Monteur mit HERO-Benutzer). Ohne Projekt = weder der Plan
          // noch die Mail kennt einen Vorgang, und keiner wird angelegt.
          const aufgabenParams = (istMonteurTask ? plan.monteur_task : a.params) as Record<string, unknown> | undefined
          const ohneMitarbeiter = istAufgabe && !istFertig(a) && monteure.isSuccess && liste.length === 0
            && !(istMonteurTask ? plan.monteur_task?.target_user_id : (a.params as Record<string, unknown> | undefined)?.target_user_id)
          const ohneProjekt = istAufgabe && !istFertig(a) && !(
            plan.project?.id || verknuepft?.hero_project_id || aufgabenParams?.project_match_id
            || gewaehlt.some((x) => x.type === "projekt_anlegen")
          )
          const zuordnungsHinweis = (ohneMitarbeiter || ohneProjekt) ? (
            <span role="alert" style={{ color: "#92400e", fontWeight: 600 }}>
              ⚠ {[ohneMitarbeiter && "ohne Mitarbeiter", ohneProjekt && "ohne Projekt"].filter(Boolean).join(" · ")}
            </span>
          ) : null
          const gewaehlterUser = zielUser[a.type] ?? standard?.hero_user_id ?? null
          // 🔑 Sieben Katalog-Aktionen legen in Wahrheit eine HERO-Aufgabe an
          //    und unterscheiden sich nur im Titel, drei schreiben einen
          //    Logbuch-Eintrag. Die Zeile zeigt deshalb den TITEL, der wirklich
          //    entsteht ("Rueckruf an Frau Musterek"), und davor eine kleine Marke
          //    fuer die Familie. Zehnmal "Aufgabe anlegen" untereinander waere
          //    schlechter als heute — man taeuscht Ordnung und verliert den
          //    Inhalt (Absprache mit Paul, 21.08.2026).
          const istAufgabenAktion = AUFGABEN_AKTIONEN.has(a.type)
          // 🔴 `aufgabe_anlegen` ist die Ausnahme: sein Titel steht NICHT in
          //    `action.params`, sondern im Plan-Schritt `monteur_task` — dort
          //    liest ihn der Executor. Wer nur `params.title` nimmt, zeigt bei
          //    der einen Aktion, um die es am haeufigsten geht, den
          //    Kategorienamen statt des Titels. Gleiche Falle wie beim
          //    Empfaenger (gemeldet 12.08.2026).
          const eigenerTitel = (
            a.type === "aufgabe_anlegen"
              ? String(plan.monteur_task?.title ?? "")
              : String((a.params as Record<string, unknown> | undefined)?.title ?? "")
          ).trim()
          return {
            type: a.type,
            label: istAufgabenAktion
              ? (eigenerTitel || vorschlagTitel(a.type, (a.params || {}) as Record<string, unknown>))
              // Bestandskunde (#84): es wird nichts angelegt, die Mail wird an den
              // vorhandenen Kontakt gehaengt — das steht jetzt als erster Punkt.
              : (a.type === "kontakt_anlegen" && plan.contact?.id ? "Kontakt verknüpfen" : label(a.type)),
            familie: istAufgabenAktion ? ("Aufgabe" as const)
              : LOGBUCH_AKTIONEN.has(a.type) ? ("Logbuch" as const) : undefined,
            fertig: istFertig(a),
            an: !istFertig(a) && istAn(a.type, abgewaehlt),
            zusatz: istAufgabe && liste.length > 0 ? (
              <>
                {zuordnungsHinweis}
                <span>Aufgabe für</span>
                <select
                  value={(istMonteurTask ? plan.monteur_task?.target_user_id : null) ?? gewaehlterUser ?? ""}
                  onClick={(ev) => ev.preventDefault()}
                  onChange={(ev) => {
                    const uid = ev.target.value ? Number(ev.target.value) : null
                    // 🔴 `aufgabe_anlegen` ist KEINE Katalog-Aktion — sie
                    // laeuft ueber den Plan-Schritt `monteur_task`, und der
                    // Executor liest den Empfaenger dort. Die Auswahl landete
                    // bisher nur in `action.params`, wo sie niemand las: die
                    // Aufgabe ging still an den Standard-Monteur (gemeldet
                    // 12.08.2026 „Mitarbeiter-Zuordnung hat nicht geklappt").
                    if (istMonteurTask) {
                      const name = liste.find((m) => m.hero_user_id === uid)?.name || ""
                      qc.setQueryData(["draft-plan", emailId], {
                        ...plan,
                        monteur_task: {
                          ...(plan.monteur_task || { title: "", body: "", due_date: null }),
                          target_user_id: uid, target_user_name: name,
                        },
                      })
                      return
                    }
                    setZielUser((s) => ({ ...s, [a.type]: uid }))
                  }}
                  aria-label={`Empfänger für ${label(a.type)}`}
                >
                  {liste.map((m) => (
                    <option key={m.id} value={m.hero_user_id!}>{m.name}</option>
                  ))}
                </select>
                {(istMonteurTask ? plan.monteur_task?.target_user_id == null : zielUser[a.type] == null)
                  && standard && <span>(Standard des Betriebs)</span>}
              </>
            ) : a.type === "kontakt_anlegen" && !plan.contact?.id && !istFertig(a) && verknuepft?.tenant_slug ? (
              <>
                {zuordnungsHinweis}
                <button type="button" className="ev2-pk-linkbtn"
                  // Die Zeile ist ein <label>: ohne preventDefault schaltete der
                  // Klick nebenbei den Haken um.
                  onClick={(ev) => { ev.preventDefault(); setVerknuepfenOffen(true) }}>
                  oder bestehenden verknüpfen
                </button>
              </>
            ) : (zuordnungsHinweis ?? undefined),
          }
        })}
        onToggleAktion={(type) => {
          // Der Haken steuert beides: was mitgeschickt wird (`actions`) und
          // den passenden Schritt im Plan. Vorher liefen die zwei
          // Mechanismen getrennt und konnten sich widersprechen.
          setAbgewaehlt((s) => umschalten(s, type))
          const key = schrittFuerAktion(type)
          if (key) {
            const jetztAn = istAn(type, abgewaehlt)
            const basis = plan.step_enabled || { contact: true, project: true, slot: true, monteur_task: true, reply: true }
            qc.setQueryData(["draft-plan", emailId], {
              ...plan,
              step_enabled: { ...basis, [key]: !jetztAn },
            })
          }
        }}
        hinweis={alleErledigt ? undefined : (
          <>
            {aktiveSlots > 1 && <span>{aktiveSlots} Termine — ab zwei werden BLOCKER angelegt.</span>}
            {kandidaten.length > 1 && (
              /* 🔴 „Immer eindeutig" stimmt nicht: bei sanitec gehoert
                 e-m@example.org zu drei Kontakten. Frueher nahm der Code den
                 ersten und warnte ueber dessen Projekt. Jetzt entscheidet der
                 Mensch — lieber ehrlich unentschieden als falsch entschieden. */
              <span className="ev2-pk-warnung">
                {kandidaten.length} Kontakte haben{" "}
                {mehrdeutigAuf === "phone" ? "diese Telefonnummer" : "diese E-Mail-Adresse"} —
                nicht eindeutig. Bitte selbst zuordnen:
                <span style={{ display: "block", marginTop: 6, fontWeight: 400 }}>
                  {kandidaten.map((k) => (
                    <button
                      key={k.id}
                      type="button"
                      className="ev2-pk-linkbtn"
                      style={{ display: "block", textAlign: "left", marginTop: 3 }}
                      onClick={() => zuordnen.mutate(k.id)}
                      disabled={zuordnen.isPending}
                    >
                      {[k.first_name, k.last_name].filter(Boolean).join(" ") || `Kontakt ${k.id}`}
                      {k.address ? ` · ${k.address}` : ""}
                      {k.phone ? ` · ${k.phone}` : ""}
                    </button>
                  ))}
                </span>
              </span>
            )}
            {projektWaereDoppelt && (
              <span className="ev2-pk-warnung">
                Der Kontakt hat bereits {vorhandeneProjekte.length === 1 ? "ein Projekt" : `${vorhandeneProjekte.length} Projekte`}
                {vorhandeneProjekte[0]?.code ? ` (${vorhandeneProjekte[0].code})` : ""} — „Projekt anlegen" erzeugt ein weiteres.
                Rechts „Zuordnen“ verknüpft stattdessen das bestehende.
              </span>
            )}
            {zeigtAntwort && textNenntAbgewaehlten && (
              <span className="ev2-pk-warnung">
                Der Antworttext nennt noch einen abgewählten Termin — Text unten anpassen.
              </span>
            )}
            {zeigtAntwort && (
              <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <input type="checkbox" checked={mitVerlauf}
                  onChange={(e) => setMitVerlauf(e.target.checked)} />
                Verlauf der Original-Mail mitsenden
              </label>
            )}
          </>
        )}
        primaeraktion={
          <>
            {alle.length === 0 ? null : alleErledigt ? (
              <span className="ev2-plan-empty">
                <Check className="w-4 h-4" /> Alles ausgeführt.
              </span>
            ) : (
              <button className="ev2-pk-primary"
                onClick={() => vorschau.mutate()}
                disabled={vorschau.isPending || execute.isPending || gewaehlt.length === 0}>
                {vorschau.isPending || execute.isPending
                  ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Einen Moment…</>
                  : <>{gewaehlt.length} Aktion{gewaehlt.length === 1 ? "" : "en"} ausführen</>}
              </button>
            )}
            {/* Der Gegenspieler zum gecachten Plan: er bleibt sonst fuer immer
                auf dem Stand seiner Entstehung. */}
            <button type="button" className="ev2-pk-linkbtn"
              onClick={() => neuPlanen.mutate(false)} disabled={neuPlanen.isPending}
              title="Fragt den Agenten erneut — mit dem heutigen Stand aus HERO">
              {neuPlanen.isPending ? "Plant neu…" : "Neu planen"}
            </button>
            {/* „Besser planen" ist weg (Paul 28.10.2026): der Planer läuft
                schon auf GPT-6 Sol, der Knopf gab nur mehr Denkzeit. */}
          </>
        }
        nebenaktion={
          <button type="button" className="ev2-pk-linkbtn ev2-pk-warnung"
            onClick={() => { if (verwerfenGefragt) { verwerfen.mutate(); setVerwerfenGefragt(false) } else setVerwerfenGefragt(true) }}
            onBlur={() => setVerwerfenGefragt(false)}
            disabled={verwerfen.isPending}
            title="Entwurf löschen und die Mail als gesehen markieren">
            {verwerfenGefragt ? "Wirklich verwerfen?" : "Verwerfen"}
          </button>
        }
      />
      )}
      {kompakt && alleSchritte && !alleErledigt && (
        <div style={{ marginTop: 6 }}>
          <button type="button" className="ev2-vorschlag-link" onClick={() => setAlleSchritte(false)}>
            Schritte ausblenden
          </button>
        </div>
      )}

      <ActionConfirmDialog
        open={ueberbuchen !== null}
        lines={ueberbuchen ?? []}
        needsDouble
        busy={execute.isPending}
        onCancel={() => {
          setUeberbuchen(null)
          toast("Plan wird neu geladen — anderer Slot wird gesucht", "error")
          qc.invalidateQueries({ queryKey: ["draft-plan", emailId] })
        }}
        onConfirm={() => { setUeberbuchen(null); execute.mutate({ forceBook: true }) }}
      />

      <ActionConfirmDialog
        open={doppelt !== null}
        lines={doppelt ?? []}
        needsDouble
        busy={execute.isPending}
        onCancel={() => setDoppelt(null)}
        onConfirm={() => {
          setDoppelt(null)
          execute.mutate({ ...letzteAusfuehrung.current, trotzDoppelt: true })
        }}
      />

      <ActionConfirmDialog
        open={dialog !== null}
        lines={dialog?.lines ?? []}
        needsDouble={dialog?.needsDouble ?? false}
        busy={execute.isPending}
        onCancel={() => setDialog(null)}
        onConfirm={() => execute.mutate(undefined)}
      />

      {verknuepft?.tenant_slug && (
        <HeroLinkModal
          open={verknuepfenOffen}
          onClose={() => setVerknuepfenOffen(false)}
          mode="email"
          itemId={emailId}
          tenantSlug={verknuepft.tenant_slug}
          invalidateKeys={[["email", String(emailId)], ["emails"]]}
        />
      )}
    </>
  )
}


// ─── Helfer ───────────────────────────────────────────────────────────────────

/**
 * Fehlende Teilobjekte auffuellen, bevor die Karte sie liest.
 *
 * `ActionPlanCard` greift direkt auf `plan.slot.start_datetime` und Konsorten
 * zu. Fehlt eines, wirft die Komponente und die ganze Detailansicht bleibt
 * leer. Im Review kann das nicht passieren — dort kommt der Plan immer frisch
 * vom Endpunkt. Der Posteingang zeigt ihn auch fuer alte, gecachte Plaene, und
 * die muessen nicht vollstaendig sein.
 */
function vollstaendig(p: ActionPlan): ActionPlan {
  const leererSlot = { start_datetime: "", duration_minutes: 45, partner_id: null }
  return {
    ...p,
    contact: p.contact ?? ({} as ActionPlan["contact"]),
    project: p.project ?? ({} as ActionPlan["project"]),
    slot: p.slot ?? leererSlot,
    slot_secondary: p.slot_secondary ?? leererSlot,
    slot_tertiary: p.slot_tertiary ?? leererSlot,
    reply: p.reply ?? { subject: "", body: "" },
  }
}



/** Das Noetigste ueber eine Mail des Verlaufs — was die Zeitleiste auch hat. */
export type ThreadMailKurz = {
  id: number
  received_at?: string | null
  email_direction?: string | null
  plan_offen?: boolean
}

/** Die neueste ANDERE Mail des Verlaufs, an der ein offener Plan haengt. */
export function planAndererMail(emailId: number, mails?: ThreadMailKurz[]): ThreadMailKurz | null {
  const andere = (mails ?? []).filter((m) => m.id !== emailId && m.plan_offen)
  if (andere.length === 0) return null
  return [...andere].sort((a, b) =>
    new Date(b.received_at || "").getTime() - new Date(a.received_at || "").getTime(),
  )[0]
}

function PlanAndererMailHinweis({ mail, onOeffnen }: {
  mail: ThreadMailKurz
  onOeffnen?: (id: number) => void
}) {
  const datum = (() => {
    try {
      return mail.received_at
        ? new Date(mail.received_at).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" })
        : ""
    } catch { return "" }
  })()
  return (
    <span style={{ fontSize: 12.5 }}>
      Der offene Plan gehört zur Mail{datum ? ` vom ${datum}` : " davor"} in diesem Verlauf.
      {onOeffnen && (
        <button type="button" className="ev2-pk-linkbtn" style={{ marginLeft: 6 }}
          onClick={() => onOeffnen(mail.id)}>
          Anzeigen
        </button>
      )}
    </span>
  )
}

/** Senden ist im Posteingang abgewaehlt — den Text liest man im Review. */
function standardAus(type: string): boolean {
  return type === "antwort_senden"
}

/** `abgewaehlt` haelt Abweichungen vom Standard — in beide Richtungen. */
function istAn(type: string, abgewaehlt: Set<string>): boolean {
  return standardAus(type) ? abgewaehlt.has(type) : !abgewaehlt.has(type)
}

function umschalten(s: Set<string>, type: string): Set<string> {
  const next = new Set(s)
  next.has(type) ? next.delete(type) : next.add(type)
  return next
}

async function fehlertext(r: Response): Promise<string> {
  try {
    const j = await r.json() as { detail?: string | { message?: string } }
    if (typeof j?.detail === "string") return j.detail
    if (j?.detail?.message) return j.detail.message
  } catch { /* kein JSON */ }
  return `HTTP ${r.status}`
}

function msg(e: unknown): string {
  return e instanceof Error ? e.message : String(e)
}
